import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PortalV2Layout from './PortalV2Layout';
import EmbeddedStripePaymentElement from '../components/EmbeddedStripePaymentElement';
import { useAuth } from '../contexts/AuthContext';
import { Receipt, AlertCircle, CheckCircle, X, RefreshCw, Search, Download } from 'lucide-react';

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// NET30 aging classifier
const getAging = (issuedOn) => {
  if (!issuedOn) return { color: 'blue', label: 'Current' };
  const issued = new Date(issuedOn);
  const due = new Date(issued); due.setDate(due.getDate() + 30);
  const days = Math.floor((Date.now() - due.getTime()) / 86400000);
  if (days <= 0) return { color: 'blue', label: 'Current' };
  if (days <= 30) return { color: 'amber', label: `${days} days late` };
  if (days <= 60) return { color: 'orange', label: `${days} days late` };
  return { color: 'red', label: `${days} days late` };
};

const agingTextColor = (c) => ({
  blue: '#60a5fa', amber: 'var(--pv2-amber-text)', orange: '#fb923c', red: 'var(--pv2-red-bright)'
}[c] || '#60a5fa');

const ClientV2Invoices = () => {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ total_outstanding: 0, total_paid: 0, unpaid_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePay, setActivePay] = useState(null); // invoice being paid
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState([]); // ids selected for bulk pay
  const [bulkPay, setBulkPay] = useState(false); // bulk payment modal open

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/portal/invoices');
      if (res.ok) {
        const d = await res.json();
        setInvoices(d.invoices || []);
        setSummary(d.summary || { total_outstanding: 0, total_paid: 0, unpaid_count: 0 });
      } else setError('Failed to load invoices');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Create a Stripe PaymentIntent for the V2-themed Payment Element.
  const createPaymentIntent = useCallback(async () => {
    if (!activePay) throw new Error('No invoice selected');
    const res = await authFetch('/api/portal/invoices/pay-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice_id: activePay.id, origin_url: window.location.origin }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || 'Could not start payment');
    }
    return await res.json();
  }, [authFetch, activePay]);

  const closePayment = () => { setActivePay(null); load(); };

  // Bulk pay: create ONE PaymentIntent for all selected invoices, then confirm.
  const createBulkIntent = useCallback(async () => {
    const res = await authFetch('/api/portal/invoices/pay-intent-bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice_ids: selected, origin_url: window.location.origin }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || 'Could not start payment');
    }
    return await res.json();
  }, [authFetch, selected]);

  const confirmBulk = useCallback(async (pi) => {
    try {
      await authFetch('/api/portal/invoices/pay-intent-bulk/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_intent_id: pi?.id }),
      });
    } catch { /* webhook/poll reconciles */ }
    setBulkPay(false);
    setSelected([]);
    load();
  }, [authFetch]);

  const toggleSelect = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const downloadPdf = async (invoiceId, jobNumber) => {
    try {
      const res = await authFetch(`/api/portal/invoices/${invoiceId}/pdf`);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Could not download invoice PDF');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice ${jobNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Could not download invoice PDF');
    }
  };

  const totalDue = parseFloat(summary.total_outstanding || 0);
  const paidCount = invoices.length - (summary.unpaid_count || 0);

  const openInvoices = invoices.filter(i => parseFloat(i.balance_due || 0) > 0 && !i.portal_paid);
  const selectedInvoices = invoices.filter(i => selected.includes(i.id));
  const selectedTotal = selectedInvoices.reduce((s, i) => s + parseFloat(i.balance_due || 0), 0);
  const allOpenSelected = openInvoices.length > 0 && openInvoices.every(i => selected.includes(i.id));
  const toggleSelectAll = () => setSelected(allOpenSelected ? [] : openInvoices.map(i => i.id));

  return (
    <PortalV2Layout
      eyebrow="Billing"
      title="Invoices"
      subtitle="Pay open invoices, view history, and download receipts."
      crumbs={<strong>Invoices</strong>}
      action={
        <button onClick={load} disabled={loading} className="pv2-btn pv2-btn-ghost" data-testid="pv2-refresh-invoices">
          <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'pv2-skeleton' : ''} /> Refresh
        </button>
      }
    >
      {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}

      <section className="pv2-stats-grid" style={{ marginBottom: 32 }}>
        <div className="pv2-stat">
          <div className="pv2-stat-label">Outstanding</div>
          <div className="pv2-stat-value" style={{ color: totalDue > 0 ? 'var(--pv2-red-bright)' : 'inherit' }}>{fmtUSD(totalDue)}</div>
          <div className="pv2-stat-meta">{summary.unpaid_count} unpaid</div>
        </div>
        <div className="pv2-stat">
          <div className="pv2-stat-label">Total Paid</div>
          <div className="pv2-stat-value" style={{ color: 'var(--pv2-green-text)' }}>{fmtUSD(summary.total_paid)}</div>
        </div>
        <div className="pv2-stat">
          <div className="pv2-stat-label">Paid Invoices</div>
          <div className="pv2-stat-value">{paidCount}</div>
        </div>
        <div className="pv2-stat">
          <div className="pv2-stat-label">Total Invoices</div>
          <div className="pv2-stat-value">{invoices.length}</div>
        </div>
      </section>

      <div style={{ position: 'relative', marginBottom: 16 }}>
        <Search size={14} strokeWidth={1.5} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--pv2-text-subtle)' }} />
        <input
          className="pv2-input"
          placeholder="Search by invoice or job number…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ paddingLeft: 36 }}
          data-testid="pv2-invoices-search"
        />
      </div>

      <div className="pv2-table-wrap">
        {openInvoices.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--pv2-ink-line)', flexWrap: 'wrap' }} data-testid="pv2-bulk-bar">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={allOpenSelected} onChange={toggleSelectAll} data-testid="pv2-select-all-open" />
              Select all open ({openInvoices.length})
            </label>
            {selected.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: 'var(--pv2-text-muted)' }}>
                  {selected.length} selected · <strong style={{ color: '#fff' }}>{fmtUSD(selectedTotal)}</strong>
                </span>
                <button onClick={() => setSelected([])} className="pv2-btn pv2-btn-ghost" style={{ padding: '8px 14px', fontSize: 10 }} data-testid="pv2-bulk-clear">Clear</button>
                <button onClick={() => setBulkPay(true)} className="pv2-btn pv2-btn-primary" style={{ padding: '8px 16px', fontSize: 11 }} data-testid="pv2-bulk-pay">
                  Pay {selected.length} — {fmtUSD(selectedTotal)}
                </button>
              </div>
            )}
          </div>
        )}
        <table className="pv2-table" data-testid="pv2-invoices-table">
          <thead>
            <tr>
              <th style={{ width: 34 }}></th>
              <th>Invoice / Job #</th>
              <th>Issued</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Balance Due</th>
              <th style={{ textAlign: 'center', width: 70 }}>PDF</th>
              <th style={{ textAlign: 'right', width: 110 }}>Pay</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="pv2-table-empty">Loading…</td></tr>}
            {!loading && invoices.length === 0 && (
              <tr><td colSpan={8} className="pv2-table-empty">
                <Receipt size={24} style={{ opacity: 0.3, marginBottom: 8 }} />
                <div>No invoices yet.</div>
              </td></tr>
            )}
            {!loading && [...invoices]
              .filter(inv => {
                if (!searchTerm) return true;
                const q = searchTerm.toLowerCase();
                return (inv.servemanager_job_number || '').toString().toLowerCase().includes(q)
                  || (inv.id || '').toLowerCase().includes(q);
              })
              .sort((a, b) => {
                // Unpaid first (regardless of issued date), then by issued date desc
                const aOpen = parseFloat(a.balance_due || 0) > 0 ? 1 : 0;
                const bOpen = parseFloat(b.balance_due || 0) > 0 ? 1 : 0;
                if (aOpen !== bOpen) return bOpen - aOpen;
                const aD = a.issued_on ? new Date(a.issued_on).getTime() : 0;
                const bD = b.issued_on ? new Date(b.issued_on).getTime() : 0;
                return bD - aD;
              })
              .map(inv => {
              const balance = parseFloat(inv.balance_due || 0);
              const isOpen = balance > 0 && !inv.portal_paid;
              const aging = isOpen ? getAging(inv.issued_on) : null;
              return (
                <tr key={inv.id}>
                  <td style={{ textAlign: 'center' }}>
                    {isOpen ? (
                      <input
                        type="checkbox"
                        checked={selected.includes(inv.id)}
                        onChange={() => toggleSelect(inv.id)}
                        data-testid={`pv2-select-${inv.id}`}
                      />
                    ) : null}
                  </td>
                  <td>
                    <Link to={`/portal/invoices/${inv.id}`} className="pv2-table-job-num">
                      #{inv.servemanager_job_number || inv.id?.slice(0,8)}
                    </Link>
                  </td>
                  <td className="pv2-mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
                    {inv.issued_on ? new Date(inv.issued_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </td>
                  <td>
                    {isOpen
                      ? <span className="pv2-pill" style={{ color: agingTextColor(aging.color) }}>{aging.label}</span>
                      : <span className="pv2-pill" style={{ color: 'var(--pv2-green-text)' }}>Paid</span>}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmtUSD(inv.total)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500, color: isOpen ? agingTextColor(aging.color) : 'var(--pv2-green-text)' }}>
                    {fmtUSD(inv.portal_paid ? 0 : balance)}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => downloadPdf(inv.id, inv.servemanager_job_number)}
                      title="Download invoice PDF"
                      className="pv2-btn pv2-btn-ghost"
                      style={{ padding: '8px 10px', fontSize: 10 }}
                      data-testid={`pv2-download-${inv.id}`}
                    >
                      <Download size={12} strokeWidth={1.5} />
                    </button>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {isOpen ? (
                      <button onClick={() => setActivePay(inv)} className="pv2-btn pv2-btn-primary" style={{ padding: '8px 14px', fontSize: 10 }} data-testid={`pv2-pay-${inv.id}`}>
                        Pay
                      </button>
                    ) : (
                      <span style={{ color: 'var(--pv2-text-subtle)', fontSize: 11 }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* NET30 helper */}
      <div className="pv2-card" style={{ marginTop: 24, padding: 18 }}>
        <div className="pv2-mono-tag" style={{ marginBottom: 8 }}>NET30 Payment Terms</div>
        <p style={{ fontSize: 13, color: 'var(--pv2-text-muted)', lineHeight: 1.6 }}>
          Invoices are due 30 days from the issued date. Day 31 and beyond is considered past due.
          <br />
          Color coding indicates aging:{' '}
          <span style={{ color: '#60a5fa', fontWeight: 500 }}>Blue = Current</span>,{' '}
          <span style={{ color: 'var(--pv2-amber-text)', fontWeight: 500 }}>Amber = 1-30 days late</span>,{' '}
          <span style={{ color: '#fb923c', fontWeight: 500 }}>Orange = 31-60 days</span>,{' '}
          <span style={{ color: 'var(--pv2-red-bright)', fontWeight: 500 }}>Red = 61+ days</span>.
        </p>
      </div>

      {/* Payment overlay — backdrop click intentionally does NOT close the modal,
          to prevent the in-flight Stripe form from being dismissed mid-payment.
          Closure is via the explicit X button only, or auto-redirect on success. */}
      {activePay && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 32 }}>
          <div style={{ width: '100%', maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, color: '#fff' }}>
              <div>
                <div className="pv2-mono-sm">Secure Payment</div>
                <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>
                  Pay Invoice — Job #{activePay.servemanager_job_number || ''}
                </div>
                <div style={{ fontSize: 13, color: 'var(--pv2-text-muted)', marginTop: 2 }}>
                  Amount: <span style={{ color: '#fff', fontWeight: 600 }}>{fmtUSD(activePay.balance_due)}</span>
                </div>
              </div>
              <button onClick={closePayment} style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', color: '#fff', padding: 8, cursor: 'pointer' }} data-testid="pv2-close-pay">
                <X size={16} />
              </button>
            </div>
            <div style={{ background: 'var(--pv2-ink)', border: '1px solid var(--pv2-ink-line)' }} data-testid="pv2-list-pay-modal">
              <EmbeddedStripePaymentElement
                createIntent={createPaymentIntent}
                returnUrl={`${window.location.origin}/payment-success?continue=${encodeURIComponent('/portal/invoices/' + activePay.id)}`}
                onSuccess={(pi) => {
                  const id = activePay.id;
                  setActivePay(null);
                  navigate(`/payment-success?payment_intent_id=${pi?.id || ''}&continue=${encodeURIComponent('/portal/invoices/' + id)}`);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk payment overlay — pays all selected invoices in one transaction */}
      {bulkPay && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 32 }}>
          <div style={{ width: '100%', maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, color: '#fff' }}>
              <div>
                <div className="pv2-mono-sm">Secure Payment</div>
                <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>
                  Pay {selected.length} Invoice{selected.length === 1 ? '' : 's'} Together
                </div>
                <div style={{ fontSize: 13, color: 'var(--pv2-text-muted)', marginTop: 2 }}>
                  Total: <span style={{ color: '#fff', fontWeight: 600 }}>{fmtUSD(selectedTotal)}</span>
                </div>
              </div>
              <button onClick={() => setBulkPay(false)} style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', color: '#fff', padding: 8, cursor: 'pointer' }} data-testid="pv2-close-bulk-pay">
                <X size={16} />
              </button>
            </div>
            <div style={{ maxHeight: 130, overflowY: 'auto', marginBottom: 12, border: '1px solid var(--pv2-ink-line)', background: 'var(--pv2-ink)' }} data-testid="pv2-bulk-summary">
              {selectedInvoices.map(inv => (
                <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--pv2-ink-line)', fontSize: 12, color: 'var(--pv2-text-muted)' }}>
                  <span>#{inv.servemanager_job_number || inv.id?.slice(0, 8)}</span>
                  <span style={{ color: '#fff' }}>{fmtUSD(inv.balance_due)}</span>
                </div>
              ))}
            </div>
            <div style={{ background: 'var(--pv2-ink)', border: '1px solid var(--pv2-ink-line)' }} data-testid="pv2-bulk-pay-modal">
              <EmbeddedStripePaymentElement
                createIntent={createBulkIntent}
                returnUrl={`${window.location.origin}/payment-success?continue=${encodeURIComponent('/portal/invoices')}`}
                onSuccess={async (pi) => {
                  await confirmBulk(pi);
                  navigate(`/payment-success?payment_intent_id=${pi?.id || ''}&continue=${encodeURIComponent('/portal/invoices')}`);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </PortalV2Layout>
  );
};

export default ClientV2Invoices;
